/**
 * Global Keyboard Shortcuts (Spec §9).
 *
 * Zwei APIs:
 *   - `useKeyboardShortcuts(defs)` — generischer Helper für beliebige
 *     Shortcut-Definitionen (Sequences via "G>D", Single-Combos via "MOD+K").
 *   - `useGlobalShortcuts(opts)` — high-level Hook, der das komplette
 *     globale Shortcut-Set für das AppShell registriert.
 *
 * Custom-Events für Page-Level-Listener:
 *   - "shortcut:filter"          — F gedrueckt (Pages öffnen Filter-Panel)
 *   - "shortcut:submit"          — ⌘Enter gedrueckt (Forms submitten)
 *   - "shortcut:bulk-approve"    — ⌘⇧A gedrueckt (Inbox bulk-approve)
 *   - "shortcut:preview-zoom"    — Space gedrueckt (Beleg-Preview Zoom)
 *   - "workspace-switcher:open"  — ⌘⇧P gedrueckt (Sidebar-WorkspaceSwitcher öffnet)
 *
 * Form-Tag-Filter: Single-Letter-Hotkeys (n, f, [, ], ?, space) feuern NICHT in
 * <input>/<textarea>/<select>/contenteditable. Modifier-Combos (⌘Enter, ⌘⇧A,
 * ⌘⇧P) feuern UEBERALL.
 */
import { useEffect, useRef } from "react";
import type { NavigateFunction } from "react-router-dom";

// ===========================================================================
// Generic Shortcut-Helper (Sequences + Combos)
// ===========================================================================

export type ShortcutHandler = (event: KeyboardEvent) => void;

export interface ShortcutDef {
  /** Uppercased key combination, e.g. "A", "MOD+K", "G>I" for sequence */
  keys: string;
  description?: string;
  group?: string;
  handler: ShortcutHandler;
  /** Wenn true, feuert auch in input/textarea/contenteditable */
  allowInInput?: boolean;
}

function isTypingContext(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

function normaliseSingleCombo(combo: string): string {
  return combo
    .split("+")
    .map((p) => p.trim().toUpperCase())
    .map((p) => {
      if (p === "CMD" || p === "META" || p === "MOD") return "MOD";
      if (p === "CTRL" || p === "CONTROL") return "MOD";
      return p;
    })
    .sort()
    .join("+");
}

function comboFromEvent(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("MOD");
  if (e.altKey) parts.push("ALT");
  if (e.shiftKey) parts.push("SHIFT");
  const k = e.key.length === 1 ? e.key.toUpperCase() : e.key.toUpperCase();
  parts.push(k === " " ? "SPACE" : k);
  return parts.sort().join("+");
}

export function useKeyboardShortcuts(shortcuts: ShortcutDef[], enabled = true) {
  const sequenceRef = useRef<{ first: string; ts: number } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const sequences = shortcuts.filter((s) => s.keys.includes(">"));
    const singles = shortcuts.filter((s) => !s.keys.includes(">"));

    function onKeyDown(e: KeyboardEvent) {
      const typing = isTypingContext(e.target);
      const combo = comboFromEvent(e);

      // Pending sequence?
      if (sequenceRef.current && Date.now() - sequenceRef.current.ts < 1200) {
        const expected = sequences.find((s) => {
          const [a, b] = s.keys.split(">").map((p) => normaliseSingleCombo(p));
          return a === sequenceRef.current!.first && b === combo;
        });
        sequenceRef.current = null;
        if (expected && (!typing || expected.allowInInput)) {
          e.preventDefault();
          expected.handler(e);
          return;
        }
      }

      // Start of sequence?
      const seqStart = sequences.find(
        (s) => normaliseSingleCombo(s.keys.split(">")[0]) === combo,
      );
      if (seqStart && !typing && !e.metaKey && !e.ctrlKey) {
        sequenceRef.current = { first: combo, ts: Date.now() };
        return;
      }

      // Single combos
      for (const s of singles) {
        if (
          normaliseSingleCombo(s.keys) === combo &&
          (!typing || s.allowInInput)
        ) {
          e.preventDefault();
          s.handler(e);
          return;
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shortcuts, enabled]);
}

export function formatShortcut(keys: string): string {
  const isMac =
    typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform);
  return keys
    .split(">")
    .map((part) =>
      part
        .split("+")
        .map((k) => {
          const up = k.trim().toUpperCase();
          if (up === "MOD") return isMac ? "⌘" : "Ctrl";
          if (up === "SHIFT") return isMac ? "⇧" : "Shift";
          if (up === "ALT") return isMac ? "⌥" : "Alt";
          if (up === "ENTER") return "↵";
          if (up === "SPACE") return "Space";
          return up;
        })
        .join(isMac ? "" : "+"),
    )
    .join(" dann ");
}

// ===========================================================================
// Global Shortcut-Set (high-level)
// ===========================================================================

/** Custom-Event-Namen für Page-Level-Listener. */
export const ShortcutEvents = {
  filter: "shortcut:filter",
  submit: "shortcut:submit",
  bulkApprove: "shortcut:bulk-approve",
  previewZoom: "shortcut:preview-zoom",
  workspaceSwitcher: "workspace-switcher:open",
} as const;

/** Helper zum Emit eines Custom-Events ohne payload. */
function emit(name: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name));
}

export interface UseGlobalShortcutsOptions {
  /** Workspace-Base wie "/workspaces/{id}" — für G-Sequences. */
  base: string;
  navigate: NavigateFunction;
  openCommand: () => void;
  openCheatsheet: () => void;
  toggleSidebar: () => void;
  expandSidebar: () => void;
}

export function useGlobalShortcuts(opts: UseGlobalShortcutsOptions) {
  const {
    base,
    navigate,
    openCommand,
    openCheatsheet,
    toggleSidebar,
    expandSidebar,
  } = opts;

  useKeyboardShortcuts([
    // Command-Palette
    { keys: "MOD+K", description: "Command Palette", handler: openCommand },
    { keys: "MOD+/", description: "Shortcut-Hilfe", handler: openCheatsheet },

    // G-Sequences (Navigation)
    { keys: "G>D", description: "Dashboard", handler: () => navigate(`${base}/dashboard`) },
    { keys: "G>I", description: "Inbox", handler: () => navigate(`${base}/inbox`) },
    { keys: "G>R", description: "Rechnungen", handler: () => navigate(`${base}/invoices`) },
    { keys: "G>W", description: "Workflows", handler: () => navigate(`${base}/workflows`) },
    { keys: "G>L", description: "Lieferanten", handler: () => navigate(`${base}/suppliers`) },
    { keys: "G>P", description: "Payment-Runs", handler: () => navigate(`${base}/payment-runs`) },
    { keys: "G>A", description: "Analytics", handler: () => navigate(`${base}/analytics`) },
    { keys: "G>S", description: "Settings", handler: () => navigate(`${base}/settings`) },

    // Globale Single-Letter (nicht in Inputs)
    {
      keys: "N",
      description: "Beleg hochladen",
      handler: () => {
        const url = new URL(window.location.href);
        url.searchParams.set("upload", "1");
        navigate(url.pathname + url.search + url.hash);
      },
    },
    {
      keys: "F",
      description: "Filter öffnen",
      handler: () => emit(ShortcutEvents.filter),
    },
    {
      keys: "[",
      description: "Sidebar einklappen/ausklappen",
      handler: toggleSidebar,
    },
    {
      keys: "]",
      description: "Sidebar ausklappen",
      handler: expandSidebar,
    },
    // `?` ist via ShortcutsCheatsheetProvider (react-hotkeys-hook) registriert
    // und toggelt das Cheatsheet — hier nicht zusaetzlich registrieren, sonst
    // entsteht ein Toggle-Loop. `openCheatsheet` aus opts wird via Sidebar-Footer
    // + UserMenu + CommandPalette explizit aufgerufen.

    {
      keys: "SPACE",
      description: "Beleg-Preview vergrößern",
      handler: () => emit(ShortcutEvents.previewZoom),
    },

    // Modifier-Combos (feuern UEBERALL — auch in Inputs)
    {
      keys: "MOD+ENTER",
      description: "Form abschicken",
      allowInInput: true,
      handler: () => emit(ShortcutEvents.submit),
    },
    {
      keys: "MOD+SHIFT+A",
      description: "Bulk-Approve",
      allowInInput: true,
      handler: () => emit(ShortcutEvents.bulkApprove),
    },
    {
      keys: "MOD+SHIFT+P",
      description: "Workspace wechseln",
      allowInInput: true,
      handler: () => emit(ShortcutEvents.workspaceSwitcher),
    },
  ]);
}
